import {
  ArrowLeft,
  CheckCircle2,
  CreditCard,
  Loader2,
  Lock as LockIcon,
  QrCode,
  WalletCards,
  X
} from "lucide-react";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { initiatePayment, setReturnURL } from "../../api/action/user";
import { updateOrder } from "../../api/action/order";
import Loading from "../LandingPage/InstitutionDomain/Enquiry/components/Loading";
import { getShoppingCartItems } from "../../api/action/cart";
import { setShoppingCartData } from "../../api/reducers/shoppingCartReducer";
import {
  buildZellePendingTransaction,
  getCurrencyForCountry,
  getDefaultGatewayPaymentMethod,
  getGatewayCredentials,
  getGatewayProvider,
  getManualPaymentDisplayDetails,
  getOfflinePaymentSavedMessage,
  getOfflineOrderStatus,
  getOfflineRedirectPath,
  getSelectedPaymentOption,
  getSelectablePaymentOptions,
  getZellePaymentMethod,
  isCashPaymentOption,
  normalizeOfflineReferenceId,
  requiresOfflineReference
} from "./paymentMethodUtils";
import { Alert as UiAlert } from "../../components/ui/Feedback";

const EMPTY_PAYMENT_METHODS = [];

const joinClasses = (...classes) => classes.filter(Boolean).join(" ");

const spacingValue = (value) => (typeof value === "number" ? `${value * 8}px` : value);

const sxToStyle = (sx = {}) => {
  const source = typeof sx === "function" ? sx({}) : sx;
  const style = {};

  Object.entries(source || {}).forEach(([key, value]) => {
    if (key.startsWith("&")) return;

    if (key === "p") style.padding = spacingValue(value);
    else if (key === "px") {
      style.paddingLeft = spacingValue(value);
      style.paddingRight = spacingValue(value);
    } else if (key === "py") {
      style.paddingTop = spacingValue(value);
      style.paddingBottom = spacingValue(value);
    } else if (key === "pt") style.paddingTop = spacingValue(value);
    else if (key === "pb") style.paddingBottom = spacingValue(value);
    else if (key === "pl") style.paddingLeft = spacingValue(value);
    else if (key === "pr") style.paddingRight = spacingValue(value);
    else if (key === "m") style.margin = spacingValue(value);
    else if (key === "mx") {
      style.marginLeft = spacingValue(value);
      style.marginRight = spacingValue(value);
    } else if (key === "my") {
      style.marginTop = spacingValue(value);
      style.marginBottom = spacingValue(value);
    } else if (key === "mt") style.marginTop = spacingValue(value);
    else if (key === "mb") style.marginBottom = spacingValue(value);
    else if (key === "ml") style.marginLeft = spacingValue(value);
    else if (key === "mr") style.marginRight = spacingValue(value);
    else if (key === "bgcolor") style.backgroundColor = value;
    else if (key === "borderRadius" && typeof value === "number") style.borderRadius = `${value * 8}px`;
    else if (key === "gap") style.gap = spacingValue(value);
    else style[key] = value;
  });

  return style;
};

const mergeStyles = (sx, style) => ({ ...sxToStyle(sx), ...(style || {}) });

const iconStyle = (sx, style, fontSize) => ({
  width: fontSize === "small" ? 16 : undefined,
  height: fontSize === "small" ? 16 : undefined,
  ...mergeStyles(sx, style),
});

const createIcon = (Icon) => ({ sx, style, fontSize, className, ...props }) => (
  <Icon className={className} style={iconStyle(sx, style, fontSize)} aria-hidden="true" {...props} />
);

const ArrowBack = createIcon(ArrowLeft);
const Payment = createIcon(CreditCard);
const Lock = createIcon(LockIcon);
const CheckCircle = createIcon(CheckCircle2);
const AccountBalanceWallet = createIcon(WalletCards);
const QrCode2 = createIcon(QrCode);

const Divider = ({ className, sx, style }) => (
  <hr className={className} style={{ border: 0, borderTop: "1px solid #e5e7eb", ...mergeStyles(sx, style) }} />
);

const getSnackbarPosition = (anchorOrigin = {}) => {
  const vertical = anchorOrigin.vertical === "top" ? "top-5" : "bottom-5";
  const horizontal = anchorOrigin.horizontal === "left"
    ? "left-5"
    : anchorOrigin.horizontal === "right"
      ? "right-5"
      : "left-1/2 -translate-x-1/2";

  return `${vertical} ${horizontal}`;
};

const Snackbar = ({
  children,
  open,
  onClose,
  autoHideDuration,
  anchorOrigin,
  className,
  sx,
  style
}) => {
  useEffect(() => {
    if (!open || !autoHideDuration || !onClose) return undefined;
    const timer = window.setTimeout(() => onClose({}, "timeout"), autoHideDuration);
    return () => window.clearTimeout(timer);
  }, [autoHideDuration, onClose, open]);

  if (!open) return null;

  return (
    <div
      className={joinClasses("fixed z-50 w-[calc(100%-2rem)] max-w-md", getSnackbarPosition(anchorOrigin), className)}
      style={mergeStyles(sx, style)}
    >
      {children}
    </div>
  );
};

const Alert = ({ children, severity = "info", onClose, className }) => (
  <div className="relative">
    <UiAlert tone={severity} className={className}>
      {children}
    </UiAlert>
    {onClose && (
      <button
        type="button"
        aria-label="Close alert"
        className="absolute right-2 top-2 rounded p-1 hover:bg-black/10"
        onClick={onClose}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    )}
  </div>
);

const Dialog = ({ children, open, onClose, PaperProps = {} }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <button
        type="button"
        aria-label="Close dialog backdrop"
        className="absolute inset-0 h-full w-full cursor-default border-0 bg-transparent p-0"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative z-10 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
        style={{
          ...mergeStyles(PaperProps.sx, PaperProps.style),
        }}
      >
        {children}
      </div>
    </div>
  );
};

const DialogTitle = ({ children, className, sx, style }) => (
  <h2 className={joinClasses("text-lg font-semibold text-slate-900", className)} style={mergeStyles(sx, style)}>
    {children}
  </h2>
);

const DialogContent = ({ children, className, sx, style }) => (
  <div className={joinClasses("mt-4", className)} style={mergeStyles(sx, style)}>
    {children}
  </div>
);

const DialogActions = ({ children, className, sx, style }) => (
  <div className={joinClasses("mt-6 flex flex-wrap justify-end gap-3", className)} style={mergeStyles(sx, style)}>
    {children}
  </div>
);

const Button = ({
  children,
  className,
  color,
  disabled,
  fullWidth,
  size,
  startIcon,
  endIcon,
  sx,
  style,
  type = "button",
  variant,
  ...props
}) => (
  <button
    type={type}
    disabled={disabled}
    className={joinClasses(
      "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition",
      variant === "contained"
        ? "bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300"
        : "text-blue-700 hover:bg-blue-50 disabled:text-slate-400",
      color === "error" && "text-red-700 hover:bg-red-50",
      size === "small" && "px-2 py-1 text-xs",
      size === "large" && "px-5 py-3 text-base",
      disabled && "cursor-not-allowed opacity-70",
      fullWidth && "w-full",
      className
    )}
    style={mergeStyles(sx, style)}
    {...props}
  >
    {startIcon}
    {children}
    {endIcon}
  </button>
);

const CircularProgress = ({ size = 20, className, sx, style }) => (
  <Loader2
    className={joinClasses("animate-spin", className)}
    style={{ width: size, height: size, ...mergeStyles(sx, style) }}
    aria-hidden="true"
  />
);

const Chip = ({ label, className, color, size, variant, sx, style }) => (
  <span
    className={joinClasses(
      "inline-flex items-center rounded-full border font-semibold",
      size === "small" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm",
      variant === "outlined" ? "border-blue-300 text-blue-700" : "border-transparent bg-blue-100 text-blue-800",
      color === "success" && "border-emerald-200 bg-emerald-50 text-emerald-700",
      className
    )}
    style={mergeStyles(sx, style)}
  >
    {label}
  </span>
);

const Tooltip = ({ children, title }) => (
  <span className="inline-flex" title={title}>
    {children}
  </span>
);

const Badge = ({ children, badgeContent, className, color, sx, style }) => (
  <span className={joinClasses("relative inline-flex items-center", className)} style={mergeStyles(sx, style)}>
    {children}
    {badgeContent && (
      <span
        className={joinClasses(
          "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
          color === "success" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
        )}
      >
        {badgeContent}
      </span>
    )}
  </span>
);

const TextField = ({
  disabled,
  fullWidth,
  helperText,
  label,
  onChange,
  placeholder,
  sx,
  style,
  value,
  ...props
}) => (
  <label className={joinClasses("block", fullWidth && "w-full")} style={mergeStyles(sx, style)}>
    {label && <span className="mb-1 block text-sm font-semibold text-slate-700">{label}</span>}
    <input
      className="input-root w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:cursor-not-allowed disabled:bg-slate-100"
      disabled={disabled}
      onChange={onChange}
      placeholder={placeholder}
      value={value}
      {...props}
    />
    {helperText && <span className="mt-1 block text-xs text-slate-500">{helperText}</span>}
  </label>
);

const Radio = ({ checked, disabled, name, onChange, value }) => (
  <input
    type="radio"
    checked={checked}
    disabled={disabled}
    name={name}
    onChange={onChange}
    value={value}
    className="h-4 w-4 border-slate-300 text-blue-600 focus:ring-blue-500"
  />
);

const RadioGroupContext = React.createContext({});

const RadioGroup = ({ children, className, name, onChange, value }) => (
  <RadioGroupContext.Provider value={{ name, onChange, value }}>
    <div role="radiogroup" className={joinClasses("flex flex-col", className)}>
      {children}
    </div>
  </RadioGroupContext.Provider>
);

const FormControlLabel = ({
  className,
  control,
  disabled,
  groupName,
  groupOnChange,
  groupValue,
  label,
  value
}) => {
  const radioGroup = React.useContext(RadioGroupContext);
  const resolvedName = groupName || radioGroup.name;
  const resolvedOnChange = groupOnChange || radioGroup.onChange;
  const resolvedValue = groupValue ?? radioGroup.value;

  return (
    <label className={joinClasses("flex cursor-pointer items-center gap-3", disabled && "cursor-not-allowed opacity-70", className)}>
      {React.isValidElement(control)
        ? React.cloneElement(control, {
          checked: resolvedValue === value,
          disabled,
          name: resolvedName,
          onChange: resolvedOnChange,
          value,
        })
        : control}
      <span className="flex-1">{label}</span>
    </label>
  );
};

const FormControl = ({ children, className, component: Component = "div" }) => (
  <Component className={className}>
    {children}
  </Component>
);

const FormLabel = ({ children, className, component: Component = "div" }) => (
  <Component className={className}>
    {children}
  </Component>
);

const OrderHistory = ({ previewOrderDetails = null, previewTotalAmount = 0 }) => {
  // State declarations
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showBalanceAlert, setShowBalanceAlert] = useState(false);
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [blockNavigation, setBlockNavigation] = useState(false);
  const [alertMsg, setAlertMsg] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [, setActiveStep] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("razorPay");
  const [showCashConfirmDialog, setShowCashConfirmDialog] = useState(false);
  const [showZelleModal, setShowZelleModal] = useState(false);
  const [isSubmittingOfflinePayment, setIsSubmittingOfflinePayment] = useState(false);
  const [zelleReferenceId, setZelleReferenceId] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Use refs to track initialization
  const initialLoadRef = useRef(false);
  const cartLoadRef = useRef(false);

  // Shipping address state
  const [shippingAddress, setShippingAddress] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    flatApartment: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    country: "",
    pinCode: "",
  });

  // Selectors
  const userDetail = useSelector((state) => state?.auth?.data);
  const companyState = useSelector((state) => state.companyReducer.company);
  const reduxOrderDetail = useSelector((state) => state.order);
  const companyOperatingLocation = companyState?.operatingLocation?.code || 'IN';
  const businessdomain = localStorage.getItem("domainType");
  const storedCompanyDetails = companyState;
  const companyPaymentMethods = storedCompanyDetails?.account?.paymentMethods || EMPTY_PAYMENT_METHODS;
  const paymentOptions = getSelectablePaymentOptions(
    companyOperatingLocation,
    companyPaymentMethods,
    storedCompanyDetails
  );
  const selectedPaymentOption = getSelectedPaymentOption(paymentOptions, paymentMethod);
  const manualPaymentOption = requiresOfflineReference(selectedPaymentOption)
    ? selectedPaymentOption
    : paymentOptions.find((option) => option.requiresReference) || null;
  const zellePaymentMethod = getZellePaymentMethod(companyPaymentMethods);
  const manualPaymentDetails = getManualPaymentDisplayDetails(
    storedCompanyDetails,
    manualPaymentOption,
    orderDetails?.orderDetails?.[0]?.number
  );
  const gatewayCredentials = getGatewayCredentials(companyPaymentMethods);
  const normalizedZelleReferenceId = normalizeOfflineReferenceId(zelleReferenceId);
  const currentPageName = location.pathname.split("/").pop() || "Home";

  // Consolidated total amount state
  const [totalAmt, setTotalAmt] = useState(0);

  const shippingCharge = 0;
  const tax = 0;
  const totalAmountToPay = totalAmt + tax + shippingCharge;
  const checkoutOrder = orderDetails?.orderDetails?.[orderDetails.orderDetails.length - 1];
  const orderSubtotal = Number(checkoutOrder?.subtotalAmount ?? checkoutOrder?.totalAmount ?? totalAmt ?? 0);
  const orderDiscountAmount = Number(checkoutOrder?.totalDiscountAmt ?? 0);

  // Set default payment method based on country - RUNS ONCE
  useEffect(() => {
    const storedPaymentMethod = localStorage.getItem("checkoutPaymentMethod");
    const availableOptions = getSelectablePaymentOptions(
      companyOperatingLocation,
      companyPaymentMethods,
      storedCompanyDetails
    );
    const defaultGatewayMethod = getDefaultGatewayPaymentMethod(
      companyOperatingLocation,
      companyPaymentMethods
    );
    const nextPaymentMethod = storedPaymentMethod && availableOptions.some((option) => option.value === storedPaymentMethod)
      ? storedPaymentMethod
      : availableOptions.find((option) => option.value === defaultGatewayMethod)?.value
        || availableOptions[0]?.value
        || "";

    setPaymentMethod(nextPaymentMethod);
    if (storedPaymentMethod) {
      localStorage.removeItem("checkoutPaymentMethod");
    }
  }, [companyOperatingLocation, companyPaymentMethods, storedCompanyDetails]);

  // Persist order data to localStorage with validation
  const persistOrderData = useCallback((orderData) => {
    try {
      if (orderData &&
        Array.isArray(orderData.orderDetails) &&
        orderData.orderDetails.length > 0) {
        localStorage.setItem('persistedOrder', JSON.stringify({
          ...orderData,
          timestamp: new Date().getTime()
        }));
      }
    } catch {
      // Storage can be unavailable in private browsing or quota-limited sessions.
    }
  }, []);

  // Load initial data with validation - RUNS ONCE
  useEffect(() => {
    // Skip if already loaded
    if (initialLoadRef.current) return;

    initialLoadRef.current = true;

    if (previewOrderDetails?.orderDetails?.length) {
      setOrderDetails(previewOrderDetails);
      const previewOrder = previewOrderDetails.orderDetails[previewOrderDetails.orderDetails.length - 1];
      setTotalAmt(parseFloat(previewOrder.totalAmtAfterDiscount ?? previewOrder.dueAmount ?? previewOrder.totalAmount ?? previewTotalAmount ?? 0));
      return;
    }

    const loadInitialData = () => {
      try {
        // Get order details from localStorage if available
        const localStorageOrderDetail = JSON.parse(localStorage.getItem("orderDetails"));
        const mergedOrderDetail = localStorageOrderDetail
          ? { orderDetails: [localStorageOrderDetail] }
          : reduxOrderDetail;

        // First try to use the merged order detail
        if (mergedOrderDetail &&
          Array.isArray(mergedOrderDetail.orderDetails) &&
          mergedOrderDetail.orderDetails.length > 0) {

          setOrderDetails(mergedOrderDetail);
          persistOrderData(mergedOrderDetail);

          const lastOrderDetail = mergedOrderDetail.orderDetails[mergedOrderDetail.orderDetails.length - 1];
          setTotalAmt(parseFloat(lastOrderDetail?.totalAmtAfterDiscount ?? lastOrderDetail?.dueAmount ?? lastOrderDetail?.totalAmount ?? 0));
          return;
        }

        // Fallback to checking persistedOrder in localStorage
        const savedOrder = localStorage.getItem('persistedOrder');
        if (savedOrder) {
          const parsedOrder = JSON.parse(savedOrder);

          const oneHourAgo = new Date().getTime() - (60 * 60 * 1000);
          if (parsedOrder &&
            Array.isArray(parsedOrder.orderDetails) &&
            parsedOrder.orderDetails.length > 0 &&
            parsedOrder.timestamp > oneHourAgo) {

            setOrderDetails(parsedOrder);

            const lastOrderDetail = parsedOrder.orderDetails[parsedOrder.orderDetails.length - 1];
            setTotalAmt(parseFloat(lastOrderDetail?.totalAmtAfterDiscount ?? lastOrderDetail?.dueAmount ?? lastOrderDetail?.totalAmount ?? 0));
            return;
          }
        }

        localStorage.removeItem('persistedOrder');
        setAlertMsg({
          open: true,
          message: "Order data is missing or expired. Please restart your order.",
          severity: "error",
        });

      } catch {
        setAlertMsg({
          open: true,
          message: "Error loading order details. Please try again.",
          severity: "error",
        });
      }
    };

    loadInitialData();
  }, [previewOrderDetails, previewTotalAmount, reduxOrderDetail, persistOrderData]);

  const authState = useSelector((state) => state?.auth?.data);

  useEffect(() => {
    // Skip if already loaded or no authState
    if (cartLoadRef.current || !authState) return;

    cartLoadRef.current = true;

    const fetchAccountDetails = async () => {
      try {
        const guest = sessionStorage.getItem("guestData");

        const userAccount = authState
          ? authState
          : guest
            ? JSON.parse(guest)
            : null;

        if (!userAccount?.account?.primaryId) {
          setAlertMsg({
            open: true,
            message: "Account details are missing. Please sign in again.",
            severity: "error",
          });
          return;
        }

        const shoppingCartId =
          userAccount.account.shoppingCart?.primaryId;

        if (!shoppingCartId) {
          setAlertMsg({
            open: true,
            message: "Shopping cart details are missing. Please restart checkout.",
            severity: "error",
          });
          return;
        }

        const shoppingCartData = await getShoppingCartItems(shoppingCartId);

        if (shoppingCartData?.[0]) {
          dispatch(setShoppingCartData(shoppingCartData[0]));

          setTotalAmt(prevTotal => {
            if (prevTotal === 0 && shoppingCartData[0]?.totalAmount) {
              return parseFloat(shoppingCartData[0].totalAmount);
            }
            return prevTotal;
          });
        }
      } catch {
        setAlertMsg({
          open: true,
          message: "Error fetching account details. Please try again.",
          severity: "error",
        });
      }
    };

    fetchAccountDetails();
  }, [authState, dispatch]);


  // User detail address effect - RUNS ONCE when userDetail changes
  useEffect(() => {
    if (userDetail && shippingAddress.firstName === "") {
      const address = userDetail.addressList && userDetail.addressList[0];
      const newAddress = {
        firstName: userDetail.firstName || "",
        lastName: userDetail.lastName || "",
        email: userDetail.email || "",
        phoneNumber: userDetail.phoneNumber || "",
        flatApartment: address?.streetName1 || "",
        street1: address?.streetName1 || "",
        street2: address?.streetName2 || "",
        city: address?.city || "",
        state: address?.state || "",
        country: address?.country || "",
        pinCode: address?.zipcode || "",
      };

      setShippingAddress(newAddress);
    }
  }, [shippingAddress.firstName, userDetail]);

  // Payment submission handler
  const submitPayment = async (paymentData, protocol, returnURL, countryCode) => {
    setLoading(true);
    setIsProcessingPayment(true);
    setBlockNavigation(true);
    setActiveStep(1);

    try {
      // Set return URL
      await setReturnURL(protocol, returnURL, countryCode);

      //  Initiate payment
      const response = await initiatePayment(paymentData);

      setLoading(false);
      setIsProcessingPayment(false);
      setActiveStep(2); // success step

      if (!response || !response.data) {
        throw new Error("Payment gateway did not respond properly");
      }

      const { data } = response;

      //  Decide payment URL
      let paymentUrl;

      if (paymentMethod === "stripe" && data.stripeResponse) {
        paymentUrl = data.stripeResponse;
      } else if (paymentMethod === "razorPay" && data.razorPayResponse) {
        paymentUrl = data.razorPayResponse;
      } else if (data.razorPayResponse) {
        paymentUrl = data.razorPayResponse;
      } else if (data.stripeResponse) {
        paymentUrl = data.stripeResponse;
      } else {
        throw new Error("No payment URL received from gateway");
      }

      // Redirect
      window.location.href = paymentUrl;

    } catch (err) {

      setLoading(false);
      setIsProcessingPayment(false);
      setBlockNavigation(false);
      setActiveStep(0);

      setAlertMsg({
        open: true,
        message: err.message || "Payment processing failed",
        severity: "error",
      });
    }
  };

  const handleCheckout = async () => {
    try {
      const order = orderDetails?.orderDetails?.[0];
      if (!order?.primaryId) {
        throw new Error("Enrollment details are missing.");
      }

      const userAccountId = userDetail?.account?.primaryId;
      const userId = userDetail?.primaryId;

      if (!userAccountId || !userId) {
        throw new Error("Student account information is missing.");
      }

      const companyId = storedCompanyDetails?.primaryId;
      const companyAccountId = storedCompanyDetails?.account?.primaryId;
      const companyDomain = storedCompanyDetails?.domain;

      if (!companyId) {
        throw new Error("Institution details are missing.");
      }

      if (isCashPaymentOption(selectedPaymentOption)) {
        setShowCashConfirmDialog(true);
        return;
      }

      if (requiresOfflineReference(selectedPaymentOption)) {
        setShowZelleModal(true);
        return;
      }

      setLoading(true);
      setIsProcessingPayment(true);

      const gatewayProvider = getGatewayProvider(paymentMethod, companyOperatingLocation, companyPaymentMethods);
      const currency = getCurrencyForCountry(companyOperatingLocation);
      // const courseNames = lineItems.map(item => getEnhancedProductDetails(item).name).join(", ");
      const customerName = `${userDetail?.firstName || ""} ${userDetail?.lastName || ""}`.trim();
      const customerEmail = userDetail?.email || "";

      const paymentData = {
        paymentMethod: "PAYMENT_GATEWAY",
        currency: currency,
        countryCode: companyOperatingLocation,
        orderNumber: order.number || order.primaryId,
        amount: totalAmt,
        accountId: userAccountId,
        userId: userId,
        // userFullName: customerName,
        transactionType: "PURCHASE",
        paymentGatewayData: {
          gatewayProvider: gatewayProvider,
          customerName: customerName,
          customerEmail: customerEmail,
          amount: totalAmt,
          // products: courseNames,
          // itemCount: lineItems.length,
          gatewayCredentials
        },
        company: {
          primaryId: companyId,
          account: {
            primaryId: companyAccountId,
            paymentMethods: storedCompanyDetails?.account?.paymentMethods || []
          },
          domain: companyDomain,
        }
      };
      const protocol = window.location.protocol;
      const returnURL = window.location.host;

      submitPayment(paymentData, protocol, returnURL, companyOperatingLocation);

    } catch (error) {
      setAlertMsg({
        open: true,
        message: error.message || "Payment failed. Please try again.",
        severity: "error",
      });
      setLoading(false);
      setIsProcessingPayment(false);
    }
  };

  // Currency symbol based on location
  const currencySymbol = () => {
    return companyOperatingLocation === "US" ? "$" : "₹";
  };

  // Navigation handler
  const handleBackNavigation = () => {
    if (blockNavigation && !isProcessingPayment) {
      setShowNavigationDialog(true);
    } else {
      navigate(-1);
    }
  };

  const getPaymentOptionIcon = (optionValue) => {
    const paymentOptionConfig = paymentOptions.find((option) => option.value === optionValue);

    if (isCashPaymentOption(paymentOptionConfig || optionValue)) {
      return <AccountBalanceWallet />;
    }

    if (requiresOfflineReference(paymentOptionConfig)) {
      return <QrCode2 />;
    }

    switch (optionValue) {
      case "cash":
        return <AccountBalanceWallet />;
      default:
        return <Payment />;
    }
  };

  const updateOfflineOrderState = (selectedMethod) => {
    setOrderDetails((prev) => {
      if (!prev?.orderDetails?.length) return prev;
      const nextStatus = getOfflineOrderStatus(selectedMethod.value);

      const updatedOrder = {
        ...prev.orderDetails[0],
        currentStatus: nextStatus,
        paymentMethod: selectedMethod.orderPaymentMethod
      };

      persistOrderData({ ...prev, orderDetails: [updatedOrder] });

      return {
        ...prev,
        orderDetails: [updatedOrder]
      };
    });
  };

  const handleOfflinePaymentRequest = async (selectedMethod) => {
    try {
      if (!selectedMethod) {
        throw new Error("Selected payment method is not available.");
      }

      const order = orderDetails?.orderDetails?.[0];
      if (!order?.primaryId) {
        throw new Error("Order details are missing.");
      }

      if (requiresOfflineReference(selectedMethod) && !normalizedZelleReferenceId) {
        throw new Error("Enter the Zelle reference ID before continuing.");
      }

      setLoading(true);
      setIsProcessingPayment(true);
      setIsSubmittingOfflinePayment(true);

      const modifiedBy = `${userDetail?.firstName || ""} ${userDetail?.lastName || ""}`.trim()
        || userDetail?.email
        || "System";
      const zelleTransaction = requiresOfflineReference(selectedMethod)
        ? buildZellePendingTransaction({
          amount: totalAmountToPay,
          orderPrimaryId: order.primaryId,
          accountPrimaryId: userDetail?.account?.primaryId || order?.accountId,
          userPrimaryId: userDetail?.primaryId,
          company: storedCompanyDetails,
          zellePaymentMethod,
          paymentMethodConfig: selectedMethod.paymentMethodConfig,
          referenceId: normalizedZelleReferenceId,
          createdBy: modifiedBy
        })
        : null;
      const nextStatus = getOfflineOrderStatus(selectedMethod.value);

      await dispatch(updateOrder({
        primaryId: Number(order.primaryId) || order.primaryId,
        currentStatus: nextStatus,
        paymentMethod: selectedMethod.orderPaymentMethod,
        modifiedBy,
        ...(zelleTransaction ? { transactionList: [zelleTransaction] } : {})
      }, "order_status_update"));

      setShowCashConfirmDialog(false);
      setShowZelleModal(false);
      setZelleReferenceId("");
      updateOfflineOrderState(selectedMethod);
      setAlertMsg({
        open: true,
        message: getOfflinePaymentSavedMessage(selectedMethod),
        severity: "success"
      });

      setTimeout(() => navigate(getOfflineRedirectPath(businessdomain)), 1500);
    } catch (error) {
      setAlertMsg({
        open: true,
        message: error.message || `Failed to update the order for ${selectedMethod.title}.`,
        severity: "error"
      });
    } finally {
      setLoading(false);
      setIsProcessingPayment(false);
      setIsSubmittingOfflinePayment(false);
    }
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return price.toFixed(2);
    }
    if (typeof price === 'string') {
      if (price.includes('.')) {
        const parts = price.split('.');
        if (parts[1].length === 1) {
          return `${parts[0]}.${parts[1]}0`;
        } else if (parts[1].length > 2) {
          return parseFloat(price).toFixed(2);
        }
        return price;
      } else {
        return `${price}.00`;
      }
    }
    return '0.00';
  };

  const getCheckoutButtonLabel = () => {
    if (isSubmittingOfflinePayment) return "Saving...";
    if (isProcessingPayment) return "Processing Payment...";
    if (isCashPaymentOption(selectedPaymentOption)) return "Confirm Cash Payment";
    if (requiresOfflineReference(selectedPaymentOption)) return `Open ${selectedPaymentOption?.title || "Payment"} Instructions`;
    return "Complete Payment";
  };

  const PaymentMethodSelector = () => {
    return (
      <FormControl id="payment-method-selector" component="fieldset" className="w-full">
        <FormLabel id="payment-method-label" component="legend" className="text-lg font-semibold text-gray-700 mb-3">
          Select Payment Method
        </FormLabel>
        <RadioGroup
          id="payment-method-group"
          aria-label="payment method"
          name="paymentMethod"
          value={paymentMethod}
          onChange={(event) => setPaymentMethod(event.target.value)}
          className="gap-4"
        >
          {paymentOptions.map((option) => (
            <div
              key={option.value}
              className={`border rounded-lg p-4 transition ${
                paymentMethod === option.value
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-blue-500"
              }`}
            >
              <FormControlLabel
                value={option.value}
                control={<Radio color="primary" />}
                label={
                  <div className="flex items-center">
                    <div className="mr-3 text-blue-600">
                      {getPaymentOptionIcon(option.value)}
                    </div>
                    <div>
                      <div>{option.title}</div>
                      <div className="text-xs text-gray-500">{option.description}</div>
                    </div>
                  </div>
                }
                className="w-full"
              />
            </div>
          ))}
        </RadioGroup>
        {requiresOfflineReference(selectedPaymentOption) && (
          <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50 p-4">
            <p className="text-sm font-semibold text-gray-900">
              {manualPaymentDetails.title} recipient: {manualPaymentDetails.recipientName}
            </p>
            <p className="mt-1 text-xs text-gray-600">{manualPaymentDetails.referenceLabel}</p>
            <Button onClick={() => setShowZelleModal(true)} sx={{ mt: 1, px: 0, textTransform: "none" }}>
              {manualPaymentDetails.qrImageUrl ? `Open ${manualPaymentDetails.title} QR` : `Open ${manualPaymentDetails.title} Details`}
            </Button>
          </div>
        )}
      </FormControl>
    );
  };

  // Loading state if order details aren't available
  if (!orderDetails || !orderDetails.orderDetails || orderDetails.orderDetails.length === 0) {
    return (
      <div id="loading-screen" className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div id="loading-message" className="text-center max-w-md">
          <CircularProgress size={60} thickness={4} className="text-blue-500" />
          <p className="mt-6 text-lg text-gray-700 font-medium">
            {alertMsg.open ? alertMsg.message : "Loading your order details..."}
          </p>
          <div id="loading-actions" className="mt-8 flex flex-wrap justify-center gap-3">
            <button
              id="refresh-button"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md flex items-center"
              onClick={() => window.location.reload()}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Page
            </button>
            <button
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition shadow-md flex items-center"
              onClick={() => navigate('/')}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Return Home
            </button>
            <button
              id="cart-button"
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-md flex items-center"
              onClick={() => navigate('/cart')}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              Back to Cart
            </button>
          </div>

          <Snackbar
            open={alertMsg.open}
            autoHideDuration={6000}
            onClose={() => setAlertMsg({ ...alertMsg, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity={alertMsg.severity} className="shadow-lg">
              {alertMsg.message}
            </Alert>
          </Snackbar>
        </div>
      </div>
    );
  }

  // Institution Domain UI
  if (businessdomain === "INSTITUTION") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F4F1EB] to-[#ECE7DA] py-12 px-4 sm:px-6 lg:px-8 font-serif">
        <Snackbar
          id="balance-alert"
          open={showBalanceAlert}
          autoHideDuration={6000}
          onClose={() => setShowBalanceAlert(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="warning" variant="filled" icon={<Lock />} className="shadow-xl">
            You have an outstanding balance. Please complete the payment.
          </Alert>
        </Snackbar>

        <Snackbar
          open={alertMsg.open}
          autoHideDuration={6000}
          onClose={() => setAlertMsg({ ...alertMsg, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={alertMsg.severity} className="shadow-xl">
            {alertMsg.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={showNavigationDialog}
          onClose={() => setShowNavigationDialog(false)}
          PaperProps={{
            style: {
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle className="text-xl font-bold text-gray-800">Payment Required</DialogTitle>
          <DialogContent>
            <div className="flex items-center space-x-3">
              <Lock className="text-yellow-500 text-2xl" />
              <span className="text-gray-700">You must complete the payment before leaving this page.</span>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowNavigationDialog(false)}
              color="primary"
              variant="contained"
              className="rounded-lg px-6 py-2"
            >
              Stay on Page
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showCashConfirmDialog}
          onClose={() => !isSubmittingOfflinePayment && setShowCashConfirmDialog(false)}
        >
          <DialogTitle>Confirm Cash Payment</DialogTitle>
          <DialogContent>
            <span className="text-sm text-gray-600">
              This will keep the order in payment pending status until the cash payment is collected and verified.
            </span>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCashConfirmDialog(false)} disabled={isSubmittingOfflinePayment}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handleOfflinePaymentRequest(paymentOptions.find((option) => option.value === "cash"))}
              disabled={isSubmittingOfflinePayment}
              startIcon={isSubmittingOfflinePayment ? <CircularProgress size={18} color="inherit" /> : <AccountBalanceWallet />}
            >
              {isSubmittingOfflinePayment ? "Saving..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showZelleModal}
          onClose={() => !isSubmittingOfflinePayment && setShowZelleModal(false)}
          PaperProps={{ sx: { maxWidth: 460, width: "100%" } }}
        >
          <DialogTitle>{manualPaymentDetails.dialogTitle}</DialogTitle>
          <DialogContent>
            <div className="rounded-2xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-indigo-700 p-5 text-white">
              <p className="text-xs uppercase tracking-[0.2em] text-white/70">{manualPaymentDetails.title}</p>
              <h4 className="mt-1 text-lg font-bold">{manualPaymentDetails.recipientName}</h4>
              <p className="mt-1 text-sm text-white/80">{manualPaymentDetails.referenceLabel}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-white/10 p-3">
                  <p className="text-xs text-white/70">Amount Due</p>
                  <p className="mt-1 text-lg font-bold">{currencySymbol()}{formatPrice(totalAmountToPay)}</p>
                </div>
                <div className="rounded-2xl bg-white/10 p-3">
                  <p className="text-xs text-white/70">Order Ref</p>
                  <p className="mt-1 text-sm font-semibold">{orderDetails?.orderDetails?.[0]?.number || "Pending"}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid place-items-center rounded-2xl border border-dashed border-violet-200 bg-violet-50 px-6 py-6 text-center">
              {manualPaymentDetails.qrImageUrl ? (
                <img
                  src={manualPaymentDetails.qrImageUrl}
                  alt={`${manualPaymentDetails.title} QR`}
                  className="h-44 w-44 rounded-3xl border border-violet-100 bg-white object-cover p-1 shadow-inner"
                />
              ) : (
                <div className="flex h-44 w-44 items-center justify-center rounded-3xl border border-violet-100 bg-white p-4 text-center text-sm text-slate-500 shadow-inner">
                  Use the saved payment details on this screen to complete the transfer.
                </div>
              )}
              <p className="mt-4 text-sm text-slate-700">
                {manualPaymentDetails.qrImageUrl
                  ? "Scan the QR code, send the payment, and keep your order number in the memo for manual verification."
                  : "Use the saved recipient details, send the payment, and keep your order number in the memo for manual verification."}
              </p>
            </div>

            <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-4">
              <p className="text-sm font-semibold text-slate-800">How to complete this payment</p>
              <div className="mt-2 space-y-2 text-sm text-slate-600">
                <p>1. Open your payment app or banking app.</p>
                <p>2. {manualPaymentDetails.qrImageUrl ? "Scan the QR or use the recipient details shown above." : "Use the recipient details shown above."}</p>
                <p>3. Send the full amount and keep your confirmation for manual verification.</p>
              </div>
            </div>
            <TextField
              fullWidth
              label={`${manualPaymentDetails.title} Reference ID`}
              placeholder={`Enter the ${manualPaymentDetails.title} confirmation ID`}
              value={zelleReferenceId}
              onChange={(event) => setZelleReferenceId(event.target.value)}
              disabled={isSubmittingOfflinePayment}
              helperText={manualPaymentDetails.helperText}
              sx={{
                mt: 3,
                '& .input-root': { bgcolor: '#fff', borderRadius: 2 }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowZelleModal(false)} disabled={isSubmittingOfflinePayment}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => handleOfflinePaymentRequest(manualPaymentOption)}
              disabled={isSubmittingOfflinePayment || !normalizedZelleReferenceId}
              startIcon={isSubmittingOfflinePayment ? <CircularProgress size={18} color="inherit" /> : <QrCode2 />}
            >
              {isSubmittingOfflinePayment ? "Saving..." : manualPaymentDetails.confirmLabel}
            </Button>
          </DialogActions>
        </Dialog>

        <div className="max-w-6xl mx-auto">
          <div className="flex items-center text-gray-700 space-x-2 mb-8">
            <button
              className={`text-[#3B4B5E] hover:text-[#2C3E50] flex items-center transition ${blockNavigation ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={handleBackNavigation}
              disabled={blockNavigation && !isProcessingPayment}
            >
              <ArrowBack className="mr-1 text-xl" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <span className="text-gray-400">/</span>
            <span className="font-semibold text-sm uppercase tracking-wider">{currentPageName}</span>
          </div>

          <div id="order-summary" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div id="order-summary-details" className="lg:col-span-2">
              <div id="order-summary-card" className="bg-white rounded-2xl shadow-lg p-6 transition duration-300 hover:shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#3B4B5E]">
                    Order Summary
                  </h2>
                  <Chip
                    label={`Order #${orderDetails.orderDetails[0]?.number || 'N/A'}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </div>

                {userDetail && (
                  <div className="bg-[#F0F4F8] p-6 rounded-lg mb-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-[#2C3E50]">Student Details</h3>
                      <Tooltip title="Verified Student">
                        <CheckCircle className="text-green-500" />
                      </Tooltip>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                      <div>
                        <p className="font-medium">Name</p>
                        <p>{userDetail.firstName} {userDetail.lastName}</p>
                      </div>
                      <div>
                        <p className="font-medium">Application No</p>
                        <p>{userDetail.primaryId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="font-medium">Email</p>
                        <p>{userDetail.email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="font-medium">Phone</p>
                        <p>{userDetail.phoneNumber || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Payment Method</h3>
                  <div className="bg-[#F0F4F8] p-6 rounded-lg border border-gray-200">
                    <PaymentMethodSelector />
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">Payment Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">{currencySymbol()}{formatPrice(orderSubtotal)}</span>
                    </div>
                    {orderDiscountAmount > 0 && (
                      <div className="flex justify-between text-emerald-700">
                        <span>Discount:</span>
                        <span className="font-medium">-{currencySymbol()}{formatPrice(orderDiscountAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax:</span>
                      <span className="font-medium">{currencySymbol()}{tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Processing Fee:</span>
                      <span className="font-medium">{currencySymbol()}0.00</span>
                    </div>
                    <Divider className="my-2" />
                    <div className="flex justify-between text-lg font-bold text-[#2C3E50]">
                      <span>Total Amount:</span>
                      <span>{currencySymbol()}{formatPrice(totalAmt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8 transition duration-300 hover:shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-[#3B4B5E]">Payment</h3>
                  <Badge badgeContent="Secure" color="success" className="text-xs" />
                </div>

                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <div className="flex items-center space-x-3">
                      <Lock className="text-blue-500 text-xl" />
                      <p className="text-sm text-blue-700">
                        Your payment information is handled securely. We do not store your payment details.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Amount Due:</span>
                      <span className="text-xl font-bold text-[#3B4B5E]">
                        {currencySymbol()}{formatPrice(totalAmt)}
                      </span>
                    </div>

                    <button
                      id="complete-payment-button"
                      onClick={handleCheckout}
                      disabled={loading || isProcessingPayment}
                      className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg ${(loading || isProcessingPayment) ? 'cursor-not-allowed' : ''}`}
                      style={{ backgroundColor: (loading || isProcessingPayment) ? '#60a5fa' : 'var(--education-secondary, #3B9394)' }}
                    >
                      {isProcessingPayment ? (
                        <>
                          <CircularProgress size={20} color="inherit" className="mr-2" />
                          {getCheckoutButtonLabel()}
                        </>
                      ) : (
                        <>
                          {getPaymentOptionIcon(paymentMethod)}
                          <span className="ml-2">{getCheckoutButtonLabel()}</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="text-xs text-gray-500 text-center pt-2">
                    By completing this payment, you agree to our Terms of Service and Privacy Policy.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default UI for other domains
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <br />    <br />
      <br />

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-19">
          <button
            className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg cursor-pointer hover:bg-blue-700 transition duration-200 ease-in-out mr-4 text-lg flex items-center shadow-md"
            onClick={handleBackNavigation}
            disabled={loading || isProcessingPayment}
          >
            <ArrowBack className="mr-2" />
            Back
          </button>
        </div>

        {loading && <Loading message="Processing payment, please wait..." />}

        <Snackbar
          open={alertMsg.open}
          autoHideDuration={6000}
          onClose={() => setAlertMsg({ ...alertMsg, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={alertMsg.severity} className="shadow-xl">
            {alertMsg.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={showCashConfirmDialog}
          onClose={() => !isSubmittingOfflinePayment && setShowCashConfirmDialog(false)}
        >
          <DialogTitle>Confirm Cash Payment</DialogTitle>
          <DialogContent>
            <span className="text-sm text-gray-600">
              This will keep the order in payment pending status until the cash payment is collected and verified.
            </span>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCashConfirmDialog(false)} disabled={isSubmittingOfflinePayment}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handleOfflinePaymentRequest(paymentOptions.find((option) => option.value === "cash"))}
              disabled={isSubmittingOfflinePayment}
              startIcon={isSubmittingOfflinePayment ? <CircularProgress size={18} color="inherit" /> : <AccountBalanceWallet />}
            >
              {isSubmittingOfflinePayment ? "Saving..." : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showZelleModal}
          onClose={() => !isSubmittingOfflinePayment && setShowZelleModal(false)}
          PaperProps={{ sx: { maxWidth: 460, width: "100%" } }}
        >
          <DialogTitle>{manualPaymentDetails.dialogTitle}</DialogTitle>
          <DialogContent>
            <div className="rounded-2xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-indigo-700 p-5 text-white">
              <p className="text-xs uppercase tracking-[0.2em] text-white/70">{manualPaymentDetails.title}</p>
              <h4 className="mt-1 text-lg font-bold">{manualPaymentDetails.recipientName}</h4>
              <p className="mt-1 text-sm text-white/80">{manualPaymentDetails.referenceLabel}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-white/10 p-3">
                  <p className="text-xs text-white/70">Amount Due</p>
                  <p className="mt-1 text-lg font-bold">{currencySymbol()}{formatPrice(totalAmountToPay)}</p>
                </div>
                <div className="rounded-2xl bg-white/10 p-3">
                  <p className="text-xs text-white/70">Order Ref</p>
                  <p className="mt-1 text-sm font-semibold">{orderDetails?.orderDetails?.[0]?.number || "Pending"}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid place-items-center rounded-2xl border border-dashed border-violet-200 bg-violet-50 px-6 py-6 text-center">
              {manualPaymentDetails.qrImageUrl ? (
                <img
                  src={manualPaymentDetails.qrImageUrl}
                  alt={`${manualPaymentDetails.title} QR`}
                  className="h-44 w-44 rounded-3xl border border-violet-100 bg-white object-cover p-1 shadow-inner"
                />
              ) : (
                <div className="flex h-44 w-44 items-center justify-center rounded-3xl border border-violet-100 bg-white p-4 text-center text-sm text-slate-500 shadow-inner">
                  Use the saved payment details on this screen to complete the transfer.
                </div>
              )}
              <p className="mt-4 text-sm text-slate-700">
                {manualPaymentDetails.qrImageUrl
                  ? "Scan the QR code, send the payment, and keep your order number in the memo for manual verification."
                  : "Use the saved recipient details, send the payment, and keep your order number in the memo for manual verification."}
              </p>
            </div>

            <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-4">
              <p className="text-sm font-semibold text-slate-800">How to complete this payment</p>
              <div className="mt-2 space-y-2 text-sm text-slate-600">
                <p>1. Open your payment app or banking app.</p>
                <p>2. {manualPaymentDetails.qrImageUrl ? "Scan the QR or use the recipient details shown above." : "Use the recipient details shown above."}</p>
                <p>3. Send the full amount and keep your confirmation for manual verification.</p>
              </div>
            </div>
            <TextField
              fullWidth
              label={`${manualPaymentDetails.title} Reference ID`}
              placeholder={`Enter the ${manualPaymentDetails.title} confirmation ID`}
              value={zelleReferenceId}
              onChange={(event) => setZelleReferenceId(event.target.value)}
              disabled={isSubmittingOfflinePayment}
              helperText={manualPaymentDetails.helperText}
              sx={{
                mt: 3,
                '& .input-root': { bgcolor: '#fff', borderRadius: 2 }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowZelleModal(false)} disabled={isSubmittingOfflinePayment}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => handleOfflinePaymentRequest(manualPaymentOption)}
              disabled={isSubmittingOfflinePayment || !normalizedZelleReferenceId}
              startIcon={isSubmittingOfflinePayment ? <CircularProgress size={18} color="inherit" /> : <QrCode2 />}
            >
              {isSubmittingOfflinePayment ? "Saving..." : manualPaymentDetails.confirmLabel}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Centered Payment Summary */}
        <div className="flex justify-center items-center min-h-[70vh]">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Header */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center justify-center">
                  <Payment className="w-5 h-5 mr-2" />
                  Payment Summary
                </h3>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-6">
                  <PaymentMethodSelector />
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">{currencySymbol()}{formatPrice(orderSubtotal)}</span>
                  </div>

                  {orderDiscountAmount > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>Discount</span>
                      <span className="font-medium">-{currencySymbol()}{formatPrice(orderDiscountAmount)}</span>
                    </div>
                  )}

                  {businessdomain === "RETAIL" && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Shipping</span>
                      <span className="font-medium">{currencySymbol()}{shippingCharge.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <span className="font-medium">{currencySymbol()}{tax.toFixed(2)}</span>
                  </div>

                  <div className="border-t border-gray-200 my-3"></div>

                  <div className="flex justify-between text-lg font-semibold text-gray-900">
                    <span>Total</span>
                    <span>{currencySymbol()}{formatPrice(totalAmt + tax + shippingCharge)}</span>
                  </div>
                </div>

                <button
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-lg font-medium flex items-center justify-center transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleCheckout}
                  disabled={loading || isProcessingPayment}
                >
                  {isProcessingPayment ? (
                    <>
                      <CircularProgress size={20} color="inherit" className="mr-2" />
                      {getCheckoutButtonLabel()}
                    </>
                  ) : (
                    <>
                      {getPaymentOptionIcon(paymentMethod)}
                      <span className="ml-2">{getCheckoutButtonLabel()}</span>
                    </>
                  )}
                </button>

                <div className="mt-4 text-center text-xs text-gray-500">
                  <p>By completing your purchase, you agree to our Terms of Service</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;
